import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'
import { saveAs } from 'file-saver'

const PAGE_W = 792
const PAGE_H = 612
const MARGIN_X = 30
const CONTENT_W = PAGE_W - MARGIN_X * 2
const FOOTER_TOP = 36

const NAVY = rgb(15 / 255, 27 / 255, 61 / 255)
const TEXT = rgb(27 / 255, 34 / 255, 54 / 255)
const MUTED = rgb(91 / 255, 100 / 255, 117 / 255)
const PANEL = rgb(243 / 255, 245 / 255, 244 / 255)
const LINE = rgb(228 / 255, 232 / 255, 230 / 255)
const GREEN = rgb(34 / 255, 107 / 255, 60 / 255)
const GREEN_BG = rgb(227 / 255, 244 / 255, 232 / 255)
const ACCENT = rgb(34 / 255, 107 / 255, 60 / 255)

const COLUMNS = [
  { key: 'name', label: 'Price level', width: 150, align: 'left' },
  { key: 'seats', label: 'Seats', width: 36, align: 'right' },
  {
    key: 'basePrice',
    label: 'Base price',
    width: 52,
    align: 'right',
    money: true,
    group: 'Pricing',
  },
  {
    key: 'offerPrice',
    label: 'Offer price',
    width: 52,
    align: 'right',
    money: true,
    group: 'Pricing',
  },
  {
    key: 'facilityFee',
    label: 'Facility fee',
    width: 52,
    align: 'right',
    money: true,
    group: 'Pricing',
  },
  {
    key: 'serviceFee',
    label: 'Service fee',
    width: 50,
    align: 'right',
    money: true,
    group: 'Added for buyer',
  },
  {
    key: 'processingFee',
    label: 'Processing (est.)',
    width: 62,
    align: 'right',
    money: true,
    group: 'Added for buyer',
  },
  {
    key: 'salesTax',
    label: 'Sales tax',
    width: 48,
    align: 'right',
    money: true,
    group: 'Added for buyer',
  },
  {
    key: 'chargeBackProtection',
    label: 'Charge back prot.',
    width: 62,
    align: 'right',
    money: true,
    group: 'Added for buyer',
  },
  {
    key: 'totalToBuyer',
    label: 'Total to buyer',
    width: 58,
    align: 'right',
    money: true,
    bold: true,
    group: 'Totals',
  },
  {
    key: 'payout',
    label: 'Your payout',
    width: 54,
    align: 'right',
    money: true,
    bold: true,
    group: 'Totals',
  },
  {
    key: 'maxPayout',
    label: 'Max payout',
    width: 56,
    align: 'right',
    money: true,
    bold: true,
    group: 'Totals',
  },
]

const PDF_SAFE = new Set(['•', '·', '—', '–', '…', '’', '‘', '“', '”'])

function pdfText(value) {
  return String(value ?? '')
    .replaceAll('→', ' – ')
    .replace(/[^\x20-\x7E]/g, (char) => (PDF_SAFE.has(char) ? char : ''))
}

function draw(page, value, options) {
  if (!value) return
  page.drawText(value, options)
}

function fit(text, font, size, maxWidth) {
  const value = pdfText(text)
  if (font.widthOfTextAtSize(value, size) <= maxWidth) return value
  let cut = value
  while (
    cut.length > 1 &&
    font.widthOfTextAtSize(`${cut}...`, size) > maxWidth
  ) {
    cut = cut.slice(0, -1)
  }
  return `${cut}...`
}

function money(report, value) {
  return report.currency(value || 0)
}

function columnX(index) {
  let x = MARGIN_X
  for (let i = 0; i < index; i += 1) x += COLUMNS[i].width
  return x
}

export async function createOfferPdf(report) {
  const pdf = await PDFDocument.create()
  const font = await pdf.embedFont(StandardFonts.Helvetica)
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold)
  const pages = []

  const headerHeight = 78
  const contentTop = () => PAGE_H - 24 - headerHeight

  const addPage = () => {
    const page = pdf.addPage([PAGE_W, PAGE_H])
    pages.push(page)
    drawPageHeader(page)
    return { page, y: contentTop() }
  }

  const drawPageHeader = (page) => {
    draw(page, 'blocktickets  /  CREATOR PANEL', {
      x: MARGIN_X,
      y: PAGE_H - 28,
      size: 8,
      font: bold,
      color: NAVY,
    })
    draw(page, fit(report.eventName, bold, 16, 460), {
      x: MARGIN_X,
      y: PAGE_H - 50,
      size: 16,
      font: bold,
      color: NAVY,
    })
    const meta = [report.eventDateLabel, report.eventStatusLabel]
      .filter(Boolean)
      .join('  -  ')
    draw(page, fit(meta, font, 9, 460), {
      x: MARGIN_X,
      y: PAGE_H - 66,
      size: 9,
      font,
      color: MUTED,
    })
    const rightTitle = 'OFFER BREAKDOWN'
    const rightW = bold.widthOfTextAtSize(rightTitle, 9)
    draw(page, rightTitle, {
      x: PAGE_W - MARGIN_X - rightW,
      y: PAGE_H - 46,
      size: 9,
      font: bold,
      color: NAVY,
    })
    const rightMeta = `Exported ${report.exportedDateLabel}  -  ${report.offerCount} ${report.offerCount === 1 ? 'offer' : 'offers'}  -  Global ticket limit ${pdfText(report.globalTicketLimitLabel)}`
    const rightMetaW = font.widthOfTextAtSize(rightMeta, 8)
    draw(page, rightMeta, {
      x: PAGE_W - MARGIN_X - rightMetaW,
      y: PAGE_H - 60,
      size: 8,
      font,
      color: MUTED,
    })
    page.drawRectangle({
      x: MARGIN_X,
      y: PAGE_H - 78,
      width: CONTENT_W,
      height: 2,
      color: NAVY,
    })
  }

  let cursor = addPage()

  const newPage = () => {
    cursor = addPage()
  }

  const room = () => cursor.y - FOOTER_TOP

  const drawTag = (page, text, x, baseline, { bg, color, border }) => {
    const size = 8
    const label = pdfText(text)
    const padX = 5
    const width = bold.widthOfTextAtSize(label, size) + padX * 2
    const height = 13
    page.drawRectangle({
      x,
      y: baseline - 3,
      width,
      height,
      color: bg,
      borderColor: border,
      borderWidth: border ? 0.6 : 0,
    })
    draw(page, label, {
      x: x + padX,
      y: baseline,
      size,
      font: bold,
      color,
    })
    return width + 4
  }

  const drawDetailStrip = (page, y, offer) => {
    const height = 36
    const top = y
    const bottom = y - height
    page.drawRectangle({
      x: MARGIN_X,
      y: bottom,
      width: CONTENT_W,
      height,
      color: PANEL,
    })
    const cells = [
      { label: 'ON-SALE WINDOW', value: offer.windowLabel, width: 250 },
      { label: 'INVENTORY SET', value: offer.inventory.label, width: 150 },
      { label: 'ACCESS CODE', value: offer.accessCodeLabel, width: 130 },
      { label: 'TICKET LIMIT', value: offer.ticketLimitLabel, width: 140 },
      { label: 'HIDDEN', value: offer.hiddenLabel, width: 62 },
    ]
    let x = MARGIN_X
    cells.forEach((cell, index) => {
      if (index > 0) {
        page.drawRectangle({
          x,
          y: bottom + 6,
          width: 0.6,
          height: height - 12,
          color: LINE,
        })
      }
      const inner = x + 8
      draw(page, cell.label, {
        x: inner,
        y: top - 13,
        size: 6.5,
        font: bold,
        color: MUTED,
      })
      draw(page, fit(cell.value || report.empty, font, 8, cell.width - 16), {
        x: inner,
        y: top - 26,
        size: 8,
        font,
        color: TEXT,
      })
      x += cell.width
    })
    return bottom - 8
  }

  const drawTableHeaders = (page, y) => {
    const groupY = y - 10
    const groups = []
    COLUMNS.forEach((column, index) => {
      if (!column.group) return
      const last = groups[groups.length - 1]
      if (!last || last.name !== column.group) {
        groups.push({ name: column.group, start: index, end: index })
      } else {
        last.end = index
      }
    })
    groups.forEach((group) => {
      const x = columnX(group.start)
      draw(page, group.name.toUpperCase(), {
        x,
        y: groupY,
        size: 6.5,
        font: bold,
        color: MUTED,
      })
    })
    const headerBottom = y - 28
    page.drawRectangle({
      x: MARGIN_X,
      y: headerBottom,
      width: CONTENT_W,
      height: 16,
      color: PANEL,
    })
    COLUMNS.forEach((column, index) => {
      const label = fit(column.label, bold, 6.5, column.width - 6)
      const textW = bold.widthOfTextAtSize(label, 6.5)
      const x =
        column.align === 'right'
          ? columnX(index) + column.width - textW - 3
          : columnX(index) + 3
      draw(page, label, {
        x,
        y: headerBottom + 5,
        size: 6.5,
        font: bold,
        color: MUTED,
      })
    })
    return headerBottom - 2
  }

  const cellText = (column, level, isTotal, offer) => {
    if (isTotal) {
      if (column.key === 'name') return 'Offer total'
      if (column.key === 'seats') return String(offer.totalSeats)
      if (column.key === 'maxPayout') return money(report, offer.totalMaxPayout)
      return ''
    }
    if (column.key === 'name') return level.name
    if (column.key === 'seats') return String(level.seats)
    if (column.money) return money(report, level[column.key])
    return ''
  }

  const drawRow = (page, y, offer, level, isTotal) => {
    const height = 16
    const bottom = y - height
    if (isTotal) {
      page.drawRectangle({
        x: MARGIN_X,
        y: bottom,
        width: CONTENT_W,
        height,
        color: PANEL,
      })
    }
    page.drawRectangle({
      x: MARGIN_X,
      y: bottom,
      width: CONTENT_W,
      height: 0.4,
      color: LINE,
    })
    COLUMNS.forEach((column, index) => {
      const raw = cellText(column, level, isTotal, offer)
      if (!raw) return
      const face = column.bold || isTotal ? bold : font
      const size = 7.5
      let text = fit(raw, face, size, column.width - 8)
      let x = columnX(index) + 3
      if (column.key === 'name' && !isTotal && level?.color) {
        const dot = hexColor(level.color)
        page.drawCircle({
          x: x + 3,
          y: bottom + 8,
          size: 3,
          color: dot,
        })
        text = fit(raw, face, size, column.width - 16)
        x += 10
      }
      if (column.align === 'right') {
        x =
          columnX(index) + column.width - face.widthOfTextAtSize(text, size) - 3
      }
      draw(page, text, {
        x,
        y: bottom + 4.5,
        size,
        font: face,
        color: TEXT,
      })
    })
    return bottom
  }

  const drawOfferTitle = (page, y, offer) => {
    page.drawRectangle({
      x: MARGIN_X,
      y: y - 14,
      width: 3,
      height: 14,
      color: ACCENT,
    })
    const name = fit(offer.name, bold, 12, 360)
    draw(page, name, {
      x: MARGIN_X + 10,
      y: y - 11,
      size: 12,
      font: bold,
      color: NAVY,
    })
    let x = MARGIN_X + 16 + bold.widthOfTextAtSize(name, 12)
    x += drawTag(page, offer.type, x, y - 10, {
      bg: PANEL,
      color: TEXT,
    })
    if (offer.statusLabel) {
      const onSale = offer.statusLabel === 'On Sale'
      x += drawTag(page, offer.statusLabel, x, y - 10, {
        bg: onSale ? GREEN_BG : PANEL,
        color: onSale ? GREEN : TEXT,
      })
    }
    if (offer.complimentary) {
      drawTag(page, 'Complimentary', x, y - 10, {
        bg: PANEL,
        color: TEXT,
      })
    }
    return y - 24
  }

  const sectionHeight = (offer) => {
    const rows = Math.max(offer.priceLevels.length, 1)
    return 24 + 44 + 30 + rows * 16 + 16 + 12
  }

  for (const offer of report.offers) {
    if (room() < sectionHeight(offer) && cursor.y < contentTop() - 4) {
      newPage()
    }
    let y = drawOfferTitle(cursor.page, cursor.y, offer)
    y = drawDetailStrip(cursor.page, y, offer)
    if (!offer.priceLevels.length) {
      draw(cursor.page, 'No price levels', {
        x: MARGIN_X,
        y: y - 12,
        size: 9,
        font,
        color: MUTED,
      })
      cursor.y = y - 28
      continue
    }
    y = drawTableHeaders(cursor.page, y)
    for (const level of offer.priceLevels) {
      if (y - 16 < FOOTER_TOP) {
        newPage()
        y = drawTableHeaders(cursor.page, cursor.y)
      }
      y = drawRow(cursor.page, y, offer, level, false)
    }
    if (y - 16 < FOOTER_TOP) {
      newPage()
      y = drawTableHeaders(cursor.page, cursor.y)
    }
    y = drawRow(cursor.page, y, offer, null, true)
    cursor.y = y - 18
  }

  if (!report.offers.length) {
    draw(cursor.page, 'No offers to export.', {
      x: MARGIN_X,
      y: cursor.y - 16,
      size: 11,
      font,
      color: MUTED,
    })
  }

  pages.forEach((page, index) => {
    const note =
      'Processing fees are estimates. Max payout is seats x your payout per ticket.'
    draw(page, note, {
      x: MARGIN_X,
      y: 18,
      size: 7.5,
      font,
      color: MUTED,
    })
    const pageLabel = `Page ${index + 1} of ${pages.length}`
    const width = font.widthOfTextAtSize(pageLabel, 7.5)
    draw(page, pageLabel, {
      x: PAGE_W - MARGIN_X - width,
      y: 18,
      size: 7.5,
      font,
      color: MUTED,
    })
  })

  return pdf.save()
}

function hexColor(value) {
  const hex = String(value || '').replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return MUTED
  return rgb(
    parseInt(hex.slice(0, 2), 16) / 255,
    parseInt(hex.slice(2, 4), 16) / 255,
    parseInt(hex.slice(4, 6), 16) / 255
  )
}

export async function downloadOfferPdf(report) {
  const bytes = await createOfferPdf(report)
  saveAs(
    new Blob([bytes], { type: 'application/pdf' }),
    `${report.fileBaseName}.pdf`
  )
}
